import React, { useState } from 'react';
import { Box, Button, ButtonBase, Chip, Dialog, DialogContent, IconButton, Paper, Stack, TextField, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DashboardChatSections from './DashboardChatSections';
import { DASHBOARD_COLORS, RangeChips, sectionCardSx, sectionTitleSx, trendHintSx } from './dashboardStyles';

const RANGES = [
  { key: 'last', label: 'Last chat' },
  { key: 'week', label: 'This week' },
  { key: 'month', label: 'This month' },
  { key: 'custom', label: 'Custom' },
];
const PERIOD = { last: 'Last chat', week: 'this week', month: 'in the last 30 days', custom: 'in this period' };

// Compact chat summary from the approved design: the latest summary in the
// chosen period, its themes, and the full list and transcripts one click away.
export default function ChatSummaryCard(props) {
  const { chatRange, setChatRange, chatCustomFrom, chatCustomTo, setChatCustomFrom, setChatCustomTo, setChatAppliedFrom, setChatAppliedTo,
    filteredChatSessions = [], formatDate, chatSummariesRef } = props;
  const [showAll, setShowAll] = useState(false);
  const latest = filteredChatSessions[0] || null;
  const themes = Array.isArray(latest?.key_themes) ? latest.key_themes.slice(0, 4) : [];
  const emotions = Array.isArray(latest?.emotions) ? latest.emotions.slice(0, 3) : [];
  const pickRange = (key) => {
    setChatRange(key);
    if (key === 'custom') { setChatCustomFrom(''); setChatCustomTo(''); setChatAppliedFrom(''); setChatAppliedTo(''); }
  };

  return (
    <Paper ref={chatSummariesRef} elevation={0} sx={{ ...sectionCardSx, height: '100%', display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
        <Typography variant="h6" sx={sectionTitleSx}>Chat summary</Typography>
        {filteredChatSessions.length > 0 && (
          <ButtonBase onClick={() => setShowAll(true)} sx={{ ...trendHintSx, fontSize: 13, p: 0.5, borderRadius: 1 }}>View all chats ›</ButtonBase>
        )}
      </Stack>
      <RangeChips options={RANGES} value={chatRange} onChange={pickRange} label="Chat summary period" />
      {chatRange === 'custom' && (
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }}>
          <TextField type="date" size="small" label="Start date" value={chatCustomFrom} onChange={(e) => setChatCustomFrom(e.target.value)} InputLabelProps={{ shrink: true }} />
          <TextField type="date" size="small" label="End date" value={chatCustomTo} onChange={(e) => setChatCustomTo(e.target.value)} InputLabelProps={{ shrink: true }} />
          <Button size="small" variant="contained" onClick={() => { setChatAppliedFrom(chatCustomFrom); setChatAppliedTo(chatCustomTo); }}
            sx={{ textTransform: 'none', fontWeight: 600, bgcolor: DASHBOARD_COLORS.accent }}>Apply</Button>
        </Stack>
      )}
      {latest ? (
        <>
          <Typography variant="body2" sx={{ color: DASHBOARD_COLORS.text, lineHeight: 1.6 }}>{latest.session_summary || 'No summary was saved for this chat.'}</Typography>
          {(themes.length > 0 || emotions.length > 0) && (
            <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
              {themes.map((theme) => <Chip key={`t-${theme}`} label={theme} size="small" sx={{ bgcolor: '#E8EEFC', color: '#1E3A8A' }} />)}
              {emotions.map((emotion) => <Chip key={`e-${emotion}`} label={emotion} size="small" sx={{ bgcolor: '#F1F4F7', color: DASHBOARD_COLORS.text }} />)}
            </Stack>
          )}
          <Typography variant="caption" sx={{ color: DASHBOARD_COLORS.muted, mt: 'auto' }}>
            Last chat {formatDate(latest.created_at)}
            {chatRange !== 'last' ? ` · ${filteredChatSessions.length} chat${filteredChatSessions.length === 1 ? '' : 's'} ${PERIOD[chatRange]}` : ''}
          </Typography>
        </>
      ) : (
        <Typography variant="body2" sx={{ color: DASHBOARD_COLORS.muted }}>No chats in this period.</Typography>
      )}

      <Dialog open={showAll} onClose={() => setShowAll(false)} maxWidth="lg" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ px: 3, pt: 2 }}>
          <Typography variant="h6" sx={sectionTitleSx}>All chats</Typography>
          <IconButton onClick={() => setShowAll(false)} aria-label="Close"><CloseIcon /></IconButton>
        </Stack>
        <DialogContent>
          <Box>
            <DashboardChatSections {...props} chatSummariesRef={undefined} />
          </Box>
        </DialogContent>
      </Dialog>
    </Paper>
  );
}
